import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { CacDetailsResponseDto, CacResponseDto, Direction, FilterMetaYearResponseDto } from '../../types/response';
import KpiCacGraph from '../kpi-cac-graph/kpi-cac-graph';
import KpiMonthRangeSelectorComponent from '../kpi-month-selector/kpi-month-range-selector';

interface MonthYearRange {
  fromYear: number;
  fromMonth: number;
  toYear: number;
  toMonth: number;
}

export interface KpiCacCardComponentProps {
  years: FilterMetaYearResponseDto[];
  range: MonthYearRange;
  cac: CacResponseDto | null;
  cacDetails: CacDetailsResponseDto | null;
  isCacLoading: boolean;
  isCacDetailsLoading: boolean;
  onRangeChange: (range: { from: { month: string; year: string }; to: { month: string; year: string } }) => void;
  className?: string;
}

const DELTA_STYLES: Record<Direction, string> = {
  [Direction.UP]: 'text-[#C0DE60]',
  [Direction.DOWN]: 'text-[#CE0909]',
  [Direction.NEUTRAL]: 'text-[#D0D5DD]'
};

function DeltaIndicator({ direction, percentage }: { direction: Direction | null; percentage: number | null }) {
  const realDirection = direction ?? Direction.NEUTRAL;
  const Icon = realDirection === Direction.UP ? ArrowUp : realDirection === Direction.DOWN ? ArrowDown : ArrowRight;

  return (
    <div className={`flex items-center gap-1 text-sm font-bold ${DELTA_STYLES[realDirection]}`}>
      <Icon size={14} />
      <span>{percentage !== null ? `${Math.abs(percentage)}%` : '–'}</span>
      <span className="text-[#888] font-normal text-xs">vs Vormonat</span>
    </div>
  );
}

const CacValueSkeleton = () => (
  <div className="flex flex-col gap-3 animate-pulse">
    <div className="h-10 w-36 bg-[#E4E7EC] rounded-full" />
    <div className="h-4 w-24 bg-[#E4E7EC] rounded-full" />
  </div>
);

const CacGraphSkeleton = () => <div className="h-[100px] w-full bg-[#E4E7EC] rounded-xl animate-pulse" />;

const KpiCacCardComponent = ({
  years,
  range,
  cac,
  cacDetails,
  isCacLoading,
  isCacDetailsLoading,
  onRangeChange,
  className
}: KpiCacCardComponentProps) => {
  const formattedValue = cac?.value !== null && cac?.value !== undefined ? `${cac.value.toLocaleString('de-DE')}€` : '–';

  return (
    <div className={`bg-white rounded-2xl border border-[#E4E7EC] p-5 ${className}`} style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
      <div className="flex justify-between gap-4 items-stretch h-full">
        {/* Left: Value */}
        <div className="flex flex-col justify-between gap-2 min-w-0 flex-1">
          <span className="text-lg font-bold text-[#12282A]">CAC</span>
          <div className="flex flex-col gap-2">
            {isCacLoading ? (
              <CacValueSkeleton />
            ) : (
              <>
                <span className="text-[36px] leading-none font-bold text-[#12282A]">{formattedValue}</span>
                <DeltaIndicator direction={cac?.deltaDirection ?? null} percentage={cac?.deltaPercentage ?? null} />
              </>
            )}
          </div>
        </div>

        {/* Right: Graph + Selector */}
        <div className="flex flex-col items-stretch gap-3 flex-1 min-w-0 h-full">
          <KpiMonthRangeSelectorComponent
            years={years}
            currentFromYear={range.fromYear}
            currentFromMonth={range.fromMonth}
            currentToYear={range.toYear}
            currentToMonth={range.toMonth}
            onChange={onRangeChange}
            disabled={isCacDetailsLoading}
          />
          {isCacDetailsLoading ? <CacGraphSkeleton /> : <KpiCacGraph items={cacDetails?.items ?? []} className="w-full h-full" />}
        </div>
      </div>
    </div>
  );
};

export default KpiCacCardComponent;
